import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createPortalUserRequestSchema, type CreatePortalUserRequest } from "@portal/schemas";
import { Button, Dialog, DialogContent, DialogHeader, Input, Label } from "@portal/ui";
import { Save } from "lucide-react";
import { useForm } from "react-hook-form";
import { createPortalUser, updateMyName, updatePortalUser } from "../../api/client";
import { USERS_KEY, meQueryKey } from "../../api/queries";
import { DialogTitleWithHint } from "../../components/Hint";
import { InlineError } from "../../components/InlineError";

/** 名前を変える相手。revision はユーザーマスタの記録の版。 */
export interface UserNameTarget {
  email: string;
  familyName: string;
  givenName: string;
  familyNameKana: string;
  givenNameKana: string;
  revision: number;
}

type FormValues = CreatePortalUserRequest;

/** よみがなのヒント(入力欄の見出しに出す)。 */
export const KANA_HINT =
  "よみがなはひらがなで入力します(カタカナはひらがなにそろえます)。一覧の並びと検索に使います。";

/**
 * 名前(姓・名とよみがな)の入力欄と保存(SPEC §2.3)。
 * - mode="self": 本人が自分の名前を変える(ユーザー設定のモーダル。2026-10-15)
 * - mode="admin": 全体の管理者が、誰の名前でも変える。target が無ければ、メールも入れて新しい人を先に登録する
 */
export function UserNameForm({
  mode,
  target,
  onSaved,
  onCancel,
}: {
  mode: "self" | "admin";
  target: UserNameTarget | null;
  onSaved?: () => void;
  /** 渡すと「キャンセル」を出す(ユーザー設定の、編集をやめるとき)。 */
  onCancel?: () => void;
}) {
  const queryClient = useQueryClient();
  const form = useForm<FormValues>({
    resolver: zodResolver(createPortalUserRequestSchema),
    defaultValues: {
      email: target?.email ?? "",
      familyName: target?.familyName ?? "",
      givenName: target?.givenName ?? "",
      familyNameKana: target?.familyNameKana ?? "",
      givenNameKana: target?.givenNameKana ?? "",
    },
  });
  const save = useMutation({
    mutationFn: async (v: FormValues) => {
      const name = {
        familyName: v.familyName,
        givenName: v.givenName,
        familyNameKana: v.familyNameKana,
        givenNameKana: v.givenNameKana,
      };
      if (mode === "self" && target) {
        await updateMyName({ ...name, revision: target.revision });
        return;
      }
      if (target) await updatePortalUser(target.email, { ...name, revision: target.revision });
      else await createPortalUser({ email: v.email, ...name });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: USERS_KEY });
      // 自分の名前が変わったら、未登録の案内なども変わる
      await queryClient.invalidateQueries({ queryKey: meQueryKey });
      onSaved?.();
    },
  });
  const field = (name: keyof FormValues, label: string) => (
    <div className="flex flex-1 flex-col gap-1.5">
      <Label htmlFor={`user-${name}`}>{label}</Label>
      <Input id={`user-${name}`} {...form.register(name)} />
      {form.formState.errors[name] && (
        <p className="text-xs text-destructive">{form.formState.errors[name]?.message}</p>
      )}
    </div>
  );
  return (
    <form
      noValidate
      className="flex flex-col gap-4"
      onSubmit={form.handleSubmit((v) => save.mutate(v))}
    >
      {!target && field("email", "メールアドレス")}
      <div className="flex gap-3">
        {field("familyName", "姓")}
        {field("givenName", "名")}
      </div>
      <div className="flex gap-3">
        {field("familyNameKana", "せい")}
        {field("givenNameKana", "めい")}
      </div>
      <InlineError error={save.error} />
      <div className="flex items-center justify-end gap-2">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} disabled={save.isPending}>
            キャンセル
          </Button>
        )}
        <Button type="submit" disabled={save.isPending}>
          <Save />
          保存
        </Button>
      </div>
    </form>
  );
}

/** 管理者が名前を編集する・新しい人を先に登録するダイアログ(管理画面の「ユーザー」)。 */
export function UserNameDialog({
  target,
  open,
  onOpenChange,
}: {
  target: UserNameTarget | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitleWithHint hint={KANA_HINT}>
            {target ? "名前を編集" : "ユーザーを追加"}
          </DialogTitleWithHint>
        </DialogHeader>
        {target && <p className="text-sm text-muted-foreground">{target.email}</p>}
        <UserNameForm mode="admin" target={target} onSaved={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  );
}
